import { Applicant } from '@common/entities';
import { ViewerSessionOrigin } from 'auth/EntrataSession';

export type JourneyNavigationSource = 'url' | 'step_change' | 'tab_change';

export function resolveRoutePageId(pathname: string, routePageId?: string): string {
  if (routePageId) {
    return routePageId;
  }

  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 3) {
    return segments[2];
  }

  return pathname;
}

export function isPreviewPath(pathname: string): boolean {
  return pathname.includes('/preview/') || pathname.includes('/previewroute/');
}

export function isLeadProfilePath(pathname: string): boolean {
  return pathname.includes('/lead-profile/');
}

export interface JourneyVisitIdentity {
  sessionOrigin: ViewerSessionOrigin;
  responseId: string;
  pageId: string;
  pathname: string;
  currentTabIndex: number;
  applicationId: string;
  orgId: number;
  propertyId: number;
  navigationSource: JourneyNavigationSource;
}

export interface JourneyVisitIdentityInput {
  pathname: string;
  routePageId?: string;
  isPreviewMode: boolean;
  isAuthenticated: boolean;
  isLeadProfile: boolean;
  sessionOrigin: string | null | undefined;
  sessionApplicant: Applicant | null | undefined;
  currentTabIndex: number;
  activeStepByTab: { [tabIndex: number]: string };
  leadProfileResponseId?: string;
  leadProfileApplicants: Array<{ responseId?: string }>;
  activeResponse?: { responseId?: string; applicationId?: string };
  leadProfileApplicationId?: string;
}

export function resolveJourneyVisitIdentity(
  input: JourneyVisitIdentityInput,
): JourneyVisitIdentity | null {
  if (input.isPreviewMode || isPreviewPath(input.pathname)) {
    return null;
  }

  const onLeadProfileRoute = isLeadProfilePath(input.pathname);
  const isTrackableSession = input.isAuthenticated || input.isLeadProfile || onLeadProfileRoute;
  if (!isTrackableSession) {
    return null;
  }

  const { sessionApplicant, sessionOrigin } = input;
  if (!sessionApplicant?.sid) {
    return null;
  }

  if (!sessionOrigin) {
    return null;
  }

  let pageId = '';
  let navigationSource: JourneyNavigationSource = 'url';
  let responseId = '';
  let applicationId = '';
  let orgId = 0;
  let propertyId = 0;

  if (input.isLeadProfile || onLeadProfileRoute) {
    pageId = input.activeStepByTab[input.currentTabIndex] ?? '';
    navigationSource = 'step_change';
    responseId =
      input.leadProfileResponseId ??
      input.leadProfileApplicants[input.currentTabIndex]?.responseId ??
      input.activeResponse?.responseId ??
      sessionApplicant.currentResponse?.responseId ??
      '';
    applicationId =
      input.leadProfileApplicationId ?? sessionApplicant.currentResponse?.applicationId ?? '';
    orgId = sessionApplicant.org?.id ?? 0;
    propertyId = sessionApplicant.propertyId ?? 0;
  } else {
    pageId = resolveRoutePageId(input.pathname, input.routePageId);
    navigationSource = 'url';
    responseId =
      input.activeResponse?.responseId ?? sessionApplicant.currentResponse?.responseId ?? '';
    applicationId =
      input.activeResponse?.applicationId ?? sessionApplicant.currentResponse?.applicationId ?? '';
    orgId = sessionApplicant.org?.id ?? (Number(input.pathname.split('/')[1]) || 0);
    propertyId = sessionApplicant.propertyId ?? (Number(input.pathname.split('/')[2]) || 0);
  }

  if (!pageId || !responseId || !applicationId) {
    return null;
  }

  return {
    sessionOrigin: sessionOrigin as ViewerSessionOrigin,
    responseId,
    pageId,
    pathname: input.pathname,
    currentTabIndex: input.currentTabIndex,
    applicationId,
    orgId,
    propertyId,
    navigationSource,
  };
}

export function buildTrackingKey(identity: JourneyVisitIdentity): string {
  return [
    identity.sessionOrigin,
    identity.responseId,
    identity.pageId,
    identity.pathname,
    identity.currentTabIndex,
  ].join('|');
}
